/**
 * @typedef {Object} AddonBlock
 * @property {string} addonVariantId
 * @property {string} propertyLabel
 * @property {string} blockKey
 * @property {string} visibleValue
 * @property {Array<{key: string, value: string}>} extraProperties
 */

class PersonalizadorCartHandler {
  constructor() {
    this.init();
  }

  /** @returns {void} */
  init() {
    const wrappers = document.querySelectorAll('.personalizador-wrapper');
    wrappers.forEach((wrapper) => this.bindWrapper(/** @type {HTMLElement} */ (wrapper)));
  }

  /**
   * @param {HTMLElement} wrapper
   * @returns {void}
   */
  bindWrapper(wrapper) {
    if (wrapper.dataset.personalizadorBound === 'true') return;

    const form = this.resolveForm(wrapper);
    if (!(form instanceof HTMLFormElement)) return;

    const syncFn = () => this.sync(wrapper, form);

    wrapper.addEventListener('input', syncFn);
    wrapper.addEventListener('change', syncFn);
    wrapper.addEventListener('personalizador:sync', syncFn);

    form.addEventListener('input', (event) => {
      const target = /** @type {HTMLElement} */ (event.target);
      if (target.matches && target.matches('input[name="id"], input[name="quantity"]')) {
        syncFn();
      }
    });

    form.addEventListener('submit', syncFn);

    wrapper.dataset.personalizadorBound = 'true';
    syncFn();
  }

  /**
   * @param {HTMLElement} wrapper
   * @returns {HTMLFormElement|null}
   */
  resolveForm(wrapper) {
    const formId = wrapper.dataset.productFormId;
    if (formId) {
      const byId = document.getElementById(formId);
      if (byId instanceof HTMLFormElement) return byId;
      if (byId instanceof HTMLElement) {
        const nested = byId.querySelector('form[action*="/cart/add"]');
        if (nested instanceof HTMLFormElement) return nested;
      }
    }

    const section = wrapper.closest('.shopify-section');
    if (section) {
      const scoped = section.querySelector('form[action*="/cart/add"]');
      if (scoped instanceof HTMLFormElement) return scoped;
    }

    const fallback = document.querySelector('form[action*="/cart/add"]');
    return fallback instanceof HTMLFormElement ? fallback : null;
  }

  /**
   * @param {HTMLElement} wrapper
   * @param {HTMLFormElement} form
   * @returns {void}
   */
  sync(wrapper, form) {
    this.clearAddonInputs(form);

    if (this.getMode(wrapper) === 'custom') {
      this.appendHidden(form, 'properties[Personalizado]', 'Sí');
    }

    const mainVariantId = this.getInputValue(form, 'input[name="id"]');
    const parentReference = this.buildParentReference(wrapper, mainVariantId);
    const quantityRaw = this.getInputValue(form, 'input[name="quantity"]');
    const quantity = Number.parseInt(quantityRaw || '1', 10);
    const safeQuantity = Number.isNaN(quantity) || quantity < 1 ? 1 : quantity;

    const blocks = Array.from(
      wrapper.querySelectorAll('.personalizador__block[data-personalizador-block-key]')
    ).filter((block) => block instanceof HTMLElement);
    const activeBlocks = blocks.filter((block) => this.isBlockActive(wrapper, block));
    this.syncParentPropertyNames(blocks, wrapper);

    const missingRequiredBlock = activeBlocks.find((block) => this.getMissingRequiredLabel(block) !== null);
    if (missingRequiredBlock) {
      const label = this.getMissingRequiredLabel(missingRequiredBlock) || 'campo obligatorio';
      this.toggleAddToCart(form, true);
      const errorTemplate = wrapper.dataset.errorRequired || 'Completa el campo obligatorio: {{ label }}.';
      this.setError(wrapper, errorTemplate.replace('{{ label }}', label), missingRequiredBlock);
      this.emitDebug(wrapper);
      return;
    }

    this.toggleAddToCart(form, false);
    this.setError(wrapper, '');

    if (!mainVariantId) {
      this.emitDebug(wrapper);
      return;
    }

    const invalidAddonBlock = activeBlocks.find((block) => {
      if (this.isBlockFree(block)) return false;
      const addonVariantId = (block.dataset.addonVariantId || '').toString().trim();
      if (!addonVariantId || addonVariantId !== String(mainVariantId)) return false;
      return this.hasBlockPersonalizationValue(block);
    });

    if (invalidAddonBlock) {
      const label = invalidAddonBlock.dataset.propertyLabel || invalidAddonBlock.dataset.personalizadorBlockKey || 'addon';
      this.toggleAddToCart(form, true);
      const errorTemplate = wrapper.dataset.errorAddonSameVariant
        || 'Configuracion invalida en "{{ label }}": el addon no puede usar la misma variante que el producto principal.';
      this.setError(wrapper, errorTemplate.replace('{{ label }}', label), invalidAddonBlock);
      this.emitDebug(wrapper);
      return;
    }

    const missingAddonConfigurationBlock = activeBlocks.find((block) => {
      if (this.isBlockFree(block)) return false;
      if (this.shouldKeepPropertyOnBase(block)) return false;
      if (!this.hasBlockPersonalizationValue(block)) return false;
      const addonVariantId = (block.dataset.addonVariantId || '').toString().trim();
      return !addonVariantId;
    });

    if (missingAddonConfigurationBlock) {
      const label = missingAddonConfigurationBlock.dataset.propertyLabel
        || missingAddonConfigurationBlock.dataset.personalizadorBlockKey
        || 'addon';
      this.toggleAddToCart(form, true);
      const errorTemplate = wrapper.dataset.errorAddonMissing
        || 'Configuracion invalida en "{{ label }}": falta el producto o variante addon para anadirlo al carrito.';
      this.setError(wrapper, errorTemplate.replace('{{ label }}', label), missingAddonConfigurationBlock);
      this.emitDebug(wrapper);
      return;
    }

    /** @type {AddonBlock[]} */
    const activeAddonBlocks = [];

    activeBlocks.forEach((block) => {
      if (this.isBlockFree(block)) return;

      const addonVariantId = (block.dataset.addonVariantId || '').toString().trim();
      if (!addonVariantId) return;

      const visibleInput = this.getVisiblePropertyInput(block);
      const propertyLabel = block.dataset.propertyLabel || this.extractPropertyLabel(visibleInput?.name || '');
      const blockKey = block.dataset.personalizadorBlockKey || 'personalizador';

      const visibleValue = this.resolveVisibleValue(visibleInput);
      const extraProperties = this.collectExtraProperties(block);
      if (!this.isMeaningfulValue(visibleValue) && extraProperties.length === 0) return;

      activeAddonBlocks.push({
        addonVariantId,
        propertyLabel,
        blockKey,
        visibleValue,
        extraProperties,
      });
    });

    if (activeAddonBlocks.length === 0) {
      this.emitDebug(wrapper);
      return;
    }

    let addonIndex = 1;
    activeAddonBlocks.forEach((addon) => {
      const { addonVariantId, propertyLabel, blockKey, visibleValue, extraProperties } = addon;

      this.appendHidden(form, `items[${addonIndex}]id`, addonVariantId);
      this.appendHidden(form, `items[${addonIndex}]quantity`, String(safeQuantity));
      this.appendHidden(form, `items[${addonIndex}]parent_id`, String(mainVariantId));
      if (parentReference) {
        this.appendHidden(form, `items[${addonIndex}]properties[Producto base]`, parentReference);
      }

      if (propertyLabel && this.isMeaningfulValue(visibleValue)) {
        this.appendHidden(form, `items[${addonIndex}]properties[${propertyLabel}]`, visibleValue);
      }

      this.appendHidden(form, `items[${addonIndex}]properties[_personalizador_block]`, blockKey);

      extraProperties.forEach((extra) => {
        this.appendHidden(form, `items[${addonIndex}]properties[${extra.key}]`, extra.value);
      });

      addonIndex += 1;
    });

    this.emitDebug(wrapper);
  }

  /**
   * @param {HTMLElement} wrapper
   * @param {HTMLElement} block
   * @returns {boolean}
   */
  isBlockActive(wrapper, block) {
    const isModeDependent = block.dataset.personalizadorModeDependent === 'true';
    if (!isModeDependent) return true;
    return this.getMode(wrapper) === 'custom';
  }

  /**
   * @param {HTMLElement} block
   * @returns {boolean}
   */
  isBlockFree(block) {
    return block.dataset.addonFree === 'true';
  }

  /**
   * @param {HTMLElement} block
   * @returns {boolean}
   */
  shouldKeepPropertyOnBase(block) {
    return block.dataset.keepPropertyOnBase === 'true';
  }

  /**
   * @param {HTMLElement} block
   * @returns {string|null} label of the missing required field, or null if nothing is missing
   */
  getMissingRequiredLabel(block) {
    const isRequired = block.dataset.required === 'true';
    if (isRequired) {
      const visibleInput = this.getVisiblePropertyInput(block);
      if (!visibleInput || !this.isMeaningfulValue(visibleInput.value)) {
        return block.dataset.propertyLabel || 'campo obligatorio';
      }
    }

    const requiredExtras = Array.from(
      block.querySelectorAll('[data-personalizador-extra-property][data-required-extra="true"]')
    );
    const missingExtra = requiredExtras.find(
      (el) => !this.isMeaningfulValue(/** @type {HTMLInputElement} */ (el).value)
    );
    if (missingExtra instanceof HTMLElement) {
      return missingExtra.dataset.propertyKey || 'campo obligatorio';
    }

    return null;
  }

  /**
   * @param {HTMLElement} block
   * @returns {HTMLInputElement|HTMLTextAreaElement|HTMLSelectElement|null}
   */
  getVisiblePropertyInput(block) {
    const input = block.querySelector('[data-personalizador-visible-property]');
    return input instanceof HTMLInputElement || input instanceof HTMLTextAreaElement || input instanceof HTMLSelectElement
      ? input
      : null;
  }

  /**
   * @param {HTMLInputElement|HTMLTextAreaElement|HTMLSelectElement|null} visibleInput
   * @returns {string}
   */
  resolveVisibleValue(visibleInput) {
    return visibleInput ? this.normalizePropertyValue(visibleInput.value) : '';
  }

  /**
   * @param {HTMLElement} block
   * @returns {Array<{key: string, value: string}>}
   */
  collectExtraProperties(block) {
    if (!(block instanceof HTMLElement)) return [];
    const inputs = block.querySelectorAll('[data-personalizador-extra-property]');
    /** @type {Array<{key: string, value: string}>} */
    const result = [];
    inputs.forEach((input) => {
      const el = /** @type {HTMLInputElement} */ (input);
      const key = el.dataset.propertyKey;
      const value = (el.value || '').toString().trim();
      if (key && value) result.push({ key, value });
    });
    return result;
  }

  /**
   * @param {HTMLElement[]} blocks
   * @param {HTMLElement} wrapper
   * @returns {void}
   */
  syncParentPropertyNames(blocks, wrapper) {
    blocks.forEach((block) => {
      const shouldKeepOnBase = this.isBlockActive(wrapper, block)
        && (this.isBlockFree(block) || this.shouldKeepPropertyOnBase(block));
      const inputs = block.querySelectorAll('[data-personalizador-visible-property], [data-personalizador-extra-property]');
      inputs.forEach((input) => this.syncInputNameByValue(
        /** @type {HTMLInputElement|HTMLTextAreaElement|HTMLSelectElement} */ (input),
        shouldKeepOnBase
      ));
    });
  }

  /**
   * @param {HTMLInputElement|HTMLTextAreaElement|HTMLSelectElement} input
   * @param {boolean} shouldKeepOnBase
   * @returns {void}
   */
  syncInputNameByValue(input, shouldKeepOnBase) {
    const currentName = input.getAttribute('name') || '';
    if (!input.dataset.originalPropertyName && currentName) {
      input.dataset.originalPropertyName = currentName;
    }

    const originalName = input.dataset.originalPropertyName || currentName;
    if (!originalName) return;

    if (!shouldKeepOnBase) {
      if (input.hasAttribute('name')) {
        input.removeAttribute('name');
      }
      return;
    }

    if (this.isMeaningfulValue(input.value)) {
      if (input.getAttribute('name') !== originalName) {
        input.setAttribute('name', originalName);
      }
    } else if (input.hasAttribute('name')) {
      input.removeAttribute('name');
    }
  }

  /**
   * @param {HTMLElement} block
   * @returns {boolean}
   */
  hasBlockPersonalizationValue(block) {
    const visibleInput = this.getVisiblePropertyInput(block);
    const visibleValue = visibleInput ? this.normalizePropertyValue(visibleInput.value) : '';
    if (this.isMeaningfulValue(visibleValue)) return true;

    return this.collectExtraProperties(block).length > 0;
  }

  /**
   * @param {string} inputName
   * @returns {string}
   */
  extractPropertyLabel(inputName) {
    if (!inputName) return '';
    const match = inputName.match(/properties\[(.+)]/);
    return match && match[1] ? match[1] : '';
  }

  /**
   * @param {string} value
   * @returns {string}
   */
  normalizePropertyValue(value) {
    return (value || '').toString().trim();
  }

  /**
   * @param {string} value
   * @returns {boolean}
   */
  isMeaningfulValue(value) {
    const normalized = this.normalizePropertyValue(value);
    if (!normalized) return false;

    if (normalized === '-') {
      return false;
    }

    return true;
  }

  /**
   * @param {HTMLElement} wrapper
   * @returns {Record<string, string>}
   */
  getVariantTitleMap(wrapper) {
    /** @type {Record<string, string>} */
    let map = {};
    try {
      const raw = wrapper.dataset.parentVariantMap || '';
      if (raw) {
        const parsed = JSON.parse(raw);
        map = parsed && typeof parsed === 'object' ? parsed : {};
      }
    } catch (_error) {
      map = {};
    }
    return map;
  }

  /**
   * @param {HTMLElement} wrapper
   * @param {string} mainVariantId
   * @returns {string}
   */
  buildParentReference(wrapper, mainVariantId) {
    if (!mainVariantId) return '';

    const productTitle = (wrapper.dataset.parentProductTitle || '').toString().trim();
    const map = this.getVariantTitleMap(wrapper);
    const variantTitleRaw = (map[String(mainVariantId)] || '').toString().trim();
    const variantTitle = variantTitleRaw.toLowerCase() === 'default title' ? '' : variantTitleRaw;

    if (!productTitle) return variantTitle;
    if (!variantTitle) return productTitle;
    return `${productTitle} - ${variantTitle}`;
  }

  /**
   * @param {HTMLElement|HTMLFormElement} root
   * @param {string} selector
   * @returns {string}
   */
  getInputValue(root, selector) {
    const input = root.querySelector(selector);
    return input instanceof HTMLInputElement ? input.value : '';
  }

  /**
   * @param {HTMLFormElement} form
   * @returns {void}
   */
  clearAddonInputs(form) {
    const existing = form.querySelectorAll('.personalizador-addon-input');
    existing.forEach((node) => node.remove());
  }

  /**
   * @param {HTMLFormElement} form
   * @param {string} name
   * @param {string} value
   * @returns {void}
   */
  appendHidden(form, name, value) {
    const input = document.createElement('input');
    input.type = 'hidden';
    input.name = name;
    input.value = value;
    input.className = 'personalizador-addon-input';
    form.appendChild(input);
  }

  /**
   * @param {HTMLFormElement} form
   * @param {boolean} disabled
   * @returns {void}
   */
  toggleAddToCart(form, disabled) {
    const button = form.querySelector('button[name="add"]');
    if (!(button instanceof HTMLButtonElement)) return;

    if (disabled) {
      button.disabled = true;
      button.dataset.personalizadorDisabled = 'true';
      return;
    }

    if (button.dataset.personalizadorDisabled === 'true') {
      delete button.dataset.personalizadorDisabled;
    }

    if (!button.dataset.personalizadorDisabled && !button.dataset.medidasDisabled) {
      button.disabled = false;
    }
  }

  /**
   * @param {HTMLElement} wrapper
   * @param {string} message
   * @param {HTMLElement} [scopeEl] - block where the error originates; its own error node is preferred over the wrapper's first one
   * @returns {void}
   */
  setError(wrapper, message, scopeEl) {
    const allNodes = Array.from(wrapper.querySelectorAll('[data-personalizador-error]')).filter(
      (node) => node instanceof HTMLElement
    );

    if (!message) {
      allNodes.forEach((node) => {
        node.textContent = '';
        node.classList.add('hidden');
      });
      return;
    }

    const scopedNode = scopeEl instanceof HTMLElement ? scopeEl.querySelector('[data-personalizador-error]') : null;
    const errorNode = scopedNode instanceof HTMLElement ? scopedNode : allNodes[0];
    if (!(errorNode instanceof HTMLElement)) return;

    allNodes.forEach((node) => {
      if (node === errorNode) return;
      node.textContent = '';
      node.classList.add('hidden');
    });

    errorNode.textContent = message;
    errorNode.classList.remove('hidden');
  }

  /**
   * @param {HTMLElement} wrapper
   * @returns {void}
   */
  emitDebug(wrapper) {
    wrapper.dispatchEvent(new CustomEvent('personalizador:sync-state', { bubbles: true }));
  }

  /**
   * @param {HTMLElement} wrapper
   * @returns {'none'|'custom'}
   */
  getMode(wrapper) {
    if (this.isFree(wrapper)) return 'custom';
    return wrapper.dataset.personalizadorMode === 'custom' ? 'custom' : 'none';
  }

  /** @param {HTMLElement} wrapper */
  isFree(wrapper) {
    return wrapper.dataset.personalizadorFree === 'true';
  }
}

document.addEventListener('DOMContentLoaded', () => {
  /** @type {any} */ (window).personalizadorCartHandler = new PersonalizadorCartHandler();
});
